import { Controller } from "@hotwired/stimulus";

// Linear 4-step wizard for creating a Prospection from the index.
//   0. Campagne & approche  — campagne, approche, support
//   1. Contact              — prénom, nom, email, tel, poste, linkedin
//   2. Entreprise           — nom_entreprise, type_entreprise, secteur
//   3. Statut & note        — statut initial, note + recap
//
// Server-side : POST /juniors/:id/prospections (flat payload). The Prospection
// controller routes the payload to the modern create branch.
export default class extends Controller {
  static targets = [
    "form", "step", "progressStep", "progressCurrent",
    "prevBtn", "nextBtn", "submitBtn", "error",
    "campagneField", "prenomField", "nomField", "emailField",
    // Contact duplicate suggestion
    "contactDup", "contactDupTitle", "contactDupList",
    // Entreprise mode (existing / new)
    "entMode", "entModeCard", "entExistingPane", "entNewPane",
    "entExistingSelect", "entNomField", "entHiddenNom", "entHiddenId",
    "entTypeField", "entSecteurField",
    "entTypeHidden", "entSecteurHidden", "entTypeSecteurPane",
    "entDup", "entDupTitle", "entDupList",
    // Recap
    "recap", "recapName", "recapCampagne", "recapEntreprise", "recapStatut",
  ];

  static values = {
    current:             { type: Number, default: 0 },
    total:               { type: Number, default: 4 },
    contactVerifyUrl:    String,
    entrepriseVerifyUrl: String,
    entreprisesPayload:  { type: Array, default: [] },
  };

  connect() {
    this._render();
    this._timers = {};
    this._aborts = {};
    // Wire the existing-entreprise Select2 picker via jQuery — Select2 fires
    // its change events on the underlying <select> through the jQuery event
    // bus, and Stimulus actions on `change` sometimes miss them depending on
    // init order. Bind explicitly with a retry while waiting for Select2.
    this._wireEntreprisePicker();
  }

  disconnect() {
    Object.values(this._aborts || {}).forEach((a) => a?.abort());
    Object.values(this._timers || {}).forEach((t) => t && clearTimeout(t));
    const $ = window.jQuery || window.$;
    if ($ && this._entSel) $(this._entSel).off("change.prospectFormWizard");
  }

  _wireEntreprisePicker(retries = 12) {
    if (!this.hasEntExistingSelectTarget) return;
    const $ = window.jQuery || window.$;
    const sel = this.entExistingSelectTarget;
    if (!$) {
      sel.addEventListener("change", (e) => this.pickExistingEntreprise(e));
      this._entSel = sel;
      return;
    }
    if (!$(sel).hasClass("select2-hidden-accessible") && retries > 0) {
      setTimeout(() => this._wireEntreprisePicker(retries - 1), 80);
      return;
    }
    $(sel).off("change.prospectFormWizard")
          .on("change.prospectFormWizard", (e) => this.pickExistingEntreprise(e));
    this._entSel = sel;
  }

  next(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    if (this.currentValue >= this.totalValue - 1) return;
    this.currentValue += 1;
    this._render();
    if (this.currentValue === this.totalValue - 1) this._refreshRecap();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue -= 1;
    this._render();
  }

  _validateCurrent() {
    this._hideError();
    if (this.currentValue === 0) {
      if (this.hasCampagneFieldTarget && !this.campagneFieldTarget.value) {
        this._showError("Choisis une campagne.");
        return false;
      }
    }
    if (this.currentValue === 1) {
      if (!this.prenomFieldTarget.value.trim()) {
        this._showError("Le prénom du contact est requis.");
        this.prenomFieldTarget.focus();
        return false;
      }
      if (!this.nomFieldTarget.value.trim()) {
        this._showError("Le nom du contact est requis.");
        this.nomFieldTarget.focus();
        return false;
      }
    }
    return true;
  }

  _render() {
    this.stepTargets.forEach((s) => {
      const isActive = Number(s.dataset.step) === this.currentValue;
      s.hidden = !isActive;
      s.classList.toggle("esm_wizard__step--active", isActive);
    });
    this.progressStepTargets.forEach((p) => {
      const i = Number(p.dataset.step);
      p.classList.toggle("esm_wizard__progress-step--active", i === this.currentValue);
      p.classList.toggle("esm_wizard__progress-step--done",   i < this.currentValue);
    });
    if (this.hasProgressCurrentTarget) this.progressCurrentTarget.textContent = this.currentValue + 1;
    this.prevBtnTarget.hidden = this.currentValue === 0;
    const isLast = this.currentValue === this.totalValue - 1;
    this.nextBtnTarget.hidden = isLast;
    this.submitBtnTarget.hidden = !isLast;
  }

  _refreshRecap() {
    if (!this.hasRecapTarget) return;
    const prenom = this.prenomFieldTarget?.value.trim() || "";
    const nom    = this.nomFieldTarget?.value.trim() || "";
    if (this.hasRecapNameTarget) this.recapNameTarget.textContent = `${prenom} ${nom}`.trim() || "—";
    if (this.hasRecapCampagneTarget && this.hasCampagneFieldTarget) {
      const opt = this.campagneFieldTarget.selectedOptions[0];
      this.recapCampagneTarget.textContent = opt?.text || "—";
    }
    if (this.hasRecapEntrepriseTarget) {
      const ent = this.element.querySelector('[name="prospection[nom_entreprise]"]')?.value.trim() || "";
      this.recapEntrepriseTarget.textContent = ent || "—";
    }
    if (this.hasRecapStatutTarget) {
      const stat = this.element.querySelector('[name="prospection[statut]"]')?.value.trim() || "";
      this.recapStatutTarget.textContent = stat || "—";
    }
  }

  // ── Duplicate detection ──────────────────────────────────────────────

  checkContactDup() {
    if (this._timers.contact) clearTimeout(this._timers.contact);
    if (!this.hasContactVerifyUrlValue) return;
    const prenom = this.prenomFieldTarget?.value.trim() || "";
    const nom    = this.nomFieldTarget?.value.trim() || "";
    if (prenom.length < 2 || nom.length < 2) { this._hideContactDup(); return; }
    this._timers.contact = setTimeout(() => {
      this._fetchDup({
        key: "contact",
        url: `${this.contactVerifyUrlValue}?prenom=${encodeURIComponent(prenom)}&nom=${encodeURIComponent(nom)}`,
        renderer: this._renderContactMatches.bind(this),
      });
    }, 350);
  }

  checkEmailDup() {
    if (this._timers.email) clearTimeout(this._timers.email);
    if (!this.hasContactVerifyUrlValue) return;
    const email = this.emailFieldTarget?.value.trim() || "";
    if (email.length < 4 || !email.includes("@")) { this._hideContactDup(); return; }
    this._timers.email = setTimeout(() => {
      this._fetchDup({
        key: "email",
        url: `${this.contactVerifyUrlValue}?email=${encodeURIComponent(email)}`,
        renderer: this._renderContactMatches.bind(this),
      });
    }, 350);
  }

  checkEntrepriseDup() {
    if (this._timers.ent) clearTimeout(this._timers.ent);
    if (!this.hasEntrepriseVerifyUrlValue) return;
    const q = this.entNomFieldTarget?.value.trim() || "";
    if (q.length < 3) { this._hideEntrepriseDup(); return; }
    this._timers.ent = setTimeout(() => {
      this._fetchDup({
        key: "ent",
        url: `${this.entrepriseVerifyUrlValue}?q=${encodeURIComponent(q)}`,
        renderer: this._renderEntrepriseMatches.bind(this),
      });
    }, 350);
  }

  _fetchDup({ key, url, renderer }) {
    if (this._aborts[key]) this._aborts[key].abort();
    this._aborts[key] = new AbortController();
    fetch(url, { headers: { Accept: "application/json" }, signal: this._aborts[key].signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => renderer(p))
      .catch(() => {});
  }

  _renderContactMatches(payload) {
    const matches = (payload && payload.matches) || [];
    if (!Array.isArray(matches) || matches.length === 0 || !this.hasContactDupTarget) {
      this._hideContactDup();
      return;
    }
    this.contactDupTitleTarget.textContent = matches.length === 1
      ? "Un contact très proche existe déjà :"
      : `${matches.length} contacts très proches existent déjà :`;
    this.contactDupListTarget.innerHTML = "";
    matches.forEach((m) => {
      const li = document.createElement("li");
      li.className = "csm_dup__item";
      const meta = [m.poste, m.entreprise].filter(Boolean).join(" · ");
      li.innerHTML = `
        <span class="csm_dup__name">${this._esc(m.prenom || "")} ${this._esc(m.nom || "")}</span>
        ${m.email ? `<span class="csm_dup__meta">${this._esc(m.email)}</span>` : ""}
        ${meta  ? `<span class="csm_dup__meta">${this._esc(meta)}</span>`  : ""}
        <a href="${this._esc(m.url)}" target="_blank" class="csm_dup__use" rel="noopener">
          <i class="fa-solid fa-arrow-up-right-from-square"></i> Voir
        </a>
      `;
      this.contactDupListTarget.appendChild(li);
    });
    this.contactDupTarget.hidden = false;
  }

  _renderEntrepriseMatches(matches) {
    if (!Array.isArray(matches) || matches.length === 0 || !this.hasEntDupTarget) {
      this._hideEntrepriseDup();
      return;
    }
    this.entDupTitleTarget.textContent = matches.length === 1
      ? "Une entreprise au nom proche existe déjà :"
      : `${matches.length} entreprises au nom proche existent déjà :`;
    this.entDupListTarget.innerHTML = "";
    matches.forEach((m) => {
      const li = document.createElement("li");
      li.className = "csm_dup__item";
      const meta = [m.type_entreprise, m.secteur, m.ville].filter(Boolean).join(" · ");
      li.innerHTML = `
        <span class="csm_dup__name">${this._esc(m.nom)}</span>
        ${meta ? `<span class="csm_dup__meta">${this._esc(meta)}</span>` : ""}
        <button type="button" class="csm_dup__use" data-id="${m.id}" data-nom="${this._esc(m.nom)}"
                data-type="${this._esc(m.type_entreprise || "")}" data-secteur="${this._esc(m.secteur || "")}">
          <i class="fa-solid fa-arrow-right"></i> Utiliser celle-ci
        </button>
      `;
      li.querySelector(".csm_dup__use").addEventListener("click", () => {
        this._switchToExistingEntreprise(m);
      });
      this.entDupListTarget.appendChild(li);
    });
    this.entDupTarget.hidden = false;
  }

  _hideContactDup() {
    if (this.hasContactDupTarget) {
      this.contactDupTarget.hidden = true;
      this.contactDupListTarget.innerHTML = "";
    }
  }

  _hideEntrepriseDup() {
    if (this.hasEntDupTarget) {
      this.entDupTarget.hidden = true;
      this.entDupListTarget.innerHTML = "";
    }
  }

  // ── Entreprise mode toggle ───────────────────────────────────────────

  switchEntrepriseMode(event) {
    const mode = event.currentTarget.value;
    this.entModeCardTargets.forEach((c) => {
      c.classList.toggle("csm_mode-card--active", c.dataset.mode === mode);
    });
    if (this.hasEntExistingPaneTarget) {
      this.entExistingPaneTarget.hidden = mode !== "existing";
      this._toggleInputs(this.entExistingPaneTarget, mode === "existing");
    }
    if (this.hasEntNewPaneTarget) {
      this.entNewPaneTarget.hidden = mode !== "new";
      this._toggleInputs(this.entNewPaneTarget, mode === "new");
    }
    // Type/secteur sont visibles uniquement en mode "nouvelle entreprise" —
    // les hidden inputs restent dans le form (avec les valeurs déjà remplies
    // par le picker en mode existant) pour que la valeur soit toujours
    // envoyée au serveur.
    if (this.hasEntTypeSecteurPaneTarget) {
      this.entTypeSecteurPaneTarget.hidden = mode !== "new";
    }
    // En mode "new", on n'est plus rattaché à une entreprise existante
    // → vider l'id pour que le serveur crée bien une nouvelle entreprise.
    if (mode === "new" && this.hasEntHiddenIdTarget) {
      this.entHiddenIdTarget.value = "";
    }
    if (mode !== "new") this._hideEntrepriseDup();
  }

  // When the user picks an existing entreprise via the Select2 picker, copy
  // its denormalised attributes into the prospection's hidden source-of-truth
  // (`entHiddenNom`) plus the visible type/secteur dropdowns.
  // En mode existant, le picker écrit directement dans les hidden inputs —
  // l'utilisateur ne voit jamais les dropdowns type/secteur (qui sont
  // réservés à la création d'une nouvelle entreprise).
  pickExistingEntreprise(event) {
    const sel = event.currentTarget;
    const id = sel.value;
    if (this.hasEntHiddenIdTarget)      this.entHiddenIdTarget.value      = id || "";
    if (!id) return;
    const ent = (this.entreprisesPayloadValue || []).find((e) => String(e.id) === String(id));
    if (!ent) return;
    if (this.hasEntHiddenNomTarget)     this.entHiddenNomTarget.value     = ent.nom || "";
    if (this.hasEntTypeHiddenTarget)    this.entTypeHiddenTarget.value    = ent.type_entreprise || "";
    if (this.hasEntSecteurHiddenTarget) this.entSecteurHiddenTarget.value = ent.secteur || "";
  }

  // Mirror visible "new mode" fields into the hidden source-of-truth at
  // every keystroke / change so the form always submits the latest value.
  syncEntNom() {
    if (!this.hasEntNomFieldTarget || !this.hasEntHiddenNomTarget) return;
    this.entHiddenNomTarget.value = this.entNomFieldTarget.value;
  }

  syncEntType() {
    if (!this.hasEntTypeFieldTarget || !this.hasEntTypeHiddenTarget) return;
    this.entTypeHiddenTarget.value = this.entTypeFieldTarget.value;
  }

  syncEntSecteur() {
    if (!this.hasEntSecteurFieldTarget || !this.hasEntSecteurHiddenTarget) return;
    this.entSecteurHiddenTarget.value = this.entSecteurFieldTarget.value;
  }

  _switchToExistingEntreprise(match) {
    const radio = this.entModeCardTargets.find((c) => c.dataset.mode === "existing")
                    ?.querySelector("input[type=radio]");
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (this.hasEntExistingSelectTarget) {
      const $ = window.jQuery || window.$;
      const sel = this.entExistingSelectTarget;
      if (!Array.from(sel.options).some((o) => String(o.value) === String(match.id))) {
        sel.appendChild(new Option(match.nom, match.id, true, true));
      }
      sel.value = match.id;
      if ($ && $.fn.select2) $(sel).trigger("change");
      else sel.dispatchEvent(new Event("change", { bubbles: true }));
      // Sync the underlying free-text fields too.
      this.pickExistingEntreprise({ currentTarget: sel });
    }
    this._hideEntrepriseDup();
  }

  _toggleInputs(pane, enabled) {
    pane.querySelectorAll("input, select, textarea").forEach((el) => { el.disabled = !enabled; });
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }

  _showError(msg) { this.errorTarget.textContent = msg; this.errorTarget.hidden = false; }
  _hideError()    { this.errorTarget.hidden = true;  this.errorTarget.textContent = ""; }
}
